import {
  faFilter,
  faInfo,
  faInfoCircle,
  faSearch
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBarsFilter,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/Button/Button';
import React, { useEffect } from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles(() => ({
  container: {
    background: 'white',
    border: '1px solid #E0E3E7',
    borderRadius: 8
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  header: {},
  headRow: {
    color: '#6A728F',
    fontSize: 14,
    borderBottom: '1px solid #F3F3F3'
  },
  tableRow: {
    fontSize: 14,
    borderBottom: '1px solid #F3F3F3'
  },
  headCell: {
    padding: '17px 22px',
    fontSize: 13,
    fontWeight: 500,
    textWrap: 'nowrap'
  },
  tableCell: {
    padding: '14px 22px',
    fontSize: 13,
    fontWeight: 400,
    textWrap: 'nowrap'
  },
  pagination: {
    padding: '14px 22px',
    display: 'flex',
    alignItems: 'center'
  },
  pageToggles: {
    display: 'flex',
    gap: '6px'
  },
  pageText: {
    marginLeft: 20,
    fontSize: 12,
    color: '#6A728F'
  },
  tableMeta: {
    padding: '12px 14px',
    borderBottom: '1px solid #EDEDED',
    justifyContent: 'space-between',
    display: 'flex'
  },
  tableSearch: {
    width: '100%',
    border: 'none',
    padding: '9px 15px',
    fontSize: 14,
    outline: 'none !important'
  },
  tableSearchContainer: {
    width: 300,
    padding: '0px 11px',
    border: '1px solid #E0E3E7',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center'
  },
  tableSearchIcon: {
    fontSize: 13
  },
  filter: {},
  emptyTable: {
    padding: 24,
    color: '#9497a3',
    fontSize: 14
  },
  emptyIcon: {
    marginRight: 10
  }
}));

export type RowCell<T> = {
  header: string;
  width?: string | number;
  minWidth?: string | number;
  format: (data: T) => any;
  onClick?: () => void;
};

export type CellPadding = {
  vertical?: number;
  horizontal?: number;
};

type Props<T> = {
  data: readonly T[];
  formatRow: RowCell<T>[];
  tableName?: string;
  padding?: CellPadding;
  pageInfo?: {
    offset: number;
    limit: number;
    given: number;
    total: number;
  };
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onSearch?: (query: string) => void;
  onFilterClick?: (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  filterActive?: boolean;
  emptyTableText?: string;
  searchValue?: string;
  fallbackLimit?: number; // The number of items to show per page when no pageInfo is provided
};

const DEFAULT_FALLBACK_LIMIT = 10; // The number of items to show per page when no pageInfo is provided

export default function Table<T>({
  data,
  formatRow,
  onNextPage,
  pageInfo,
  onPreviousPage,
  tableName,
  padding,
  onFilterClick,
  filterActive,
  onSearch,
  searchValue,
  emptyTableText,
  fallbackLimit
}: Props<T>) {
  const classes = useStyles();

  const [searchSelected, setSearchSelected] = React.useState<boolean>(false);
  const [savedSearchValue, setSavedSearchValue] = React.useState<string>('');

  const [fallbackPageInfo, setFallbackPageInfo] = React.useState<{
    offset: number;
    limit: number;
    given: number;
    total: number;
  }>({
    offset: 0,
    limit: fallbackLimit ?? DEFAULT_FALLBACK_LIMIT,
    given: fallbackLimit ?? DEFAULT_FALLBACK_LIMIT,
    total: data.length
  });

  useEffect(() => {
    if (searchValue == undefined) {
      return;
    }
    setSavedSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    setFallbackPageInfo({
      offset: 0,
      limit: fallbackLimit ?? DEFAULT_FALLBACK_LIMIT,
      given: fallbackLimit ?? DEFAULT_FALLBACK_LIMIT,
      total: data.length
    });
  }, [data]);

  let outputData = data;
  if (!pageInfo && !onNextPage && !onPreviousPage) {
    onNextPage = () => {
      if (fallbackPageInfo.offset + fallbackPageInfo.limit >= data.length) {
        return;
      }

      setFallbackPageInfo({
        ...fallbackPageInfo,
        offset: fallbackPageInfo.offset + fallbackPageInfo.limit
      });
    };
    onPreviousPage = () => {
      if (fallbackPageInfo.offset - fallbackPageInfo.limit < 0) {
        setFallbackPageInfo({
          ...fallbackPageInfo,
          offset: 0
        });

        return;
      }

      setFallbackPageInfo({
        ...fallbackPageInfo,
        offset: fallbackPageInfo.offset - fallbackPageInfo.limit
      });
    };

    outputData = data.slice(
      fallbackPageInfo.offset,
      fallbackPageInfo.offset + fallbackPageInfo.limit
    );

    pageInfo = fallbackPageInfo;
  }

  return (
    <div className={classes.container}>
      <div className={classes.tableMeta}>
        {onSearch ? (
          <div
            className={classes.tableSearchContainer}
            style={{
              border: searchSelected ? '1px solid #9fa3a9' : '1px solid #E0E3E7'
            }}
          >
            <FontAwesomeIcon
              icon={faSearch}
              className={classes.tableSearchIcon}
            />
            <input
              onFocus={() => setSearchSelected(true)}
              onBlur={() => setSearchSelected(false)}
              value={savedSearchValue}
              onChange={(e) => {
                setSavedSearchValue(e.target.value);
                onSearch && onSearch(e.target.value);
              }}
              className={classes.tableSearch}
              placeholder={`Search ${tableName}`}
            />
          </div>
        ) : (
          <div />
        )}
        {onFilterClick ? (
          <div className={classes.filter}>
            <Button
              archetype="flat"
              onClick={onFilterClick}
              active={filterActive}
            >
              <FontAwesomeIcon icon={faBarsFilter} /> Filter
            </Button>
          </div>
        ) : (
          <div />
        )}
      </div>
      <table className={classes.table}>
        <thead className={classes.header}>
          <tr className={classes.headRow}>
            {formatRow.map((row) => (
              <td
                className={classes.headCell}
                style={{ width: row.width, minWidth: row.minWidth }}
              >
                {row.header}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {outputData.map((item) => (
            <tr className={classes.tableRow}>
              {formatRow.map((row) => (
                <td
                  className={classes.tableCell}
                  style={
                    padding && {
                      paddingLeft: padding.horizontal,
                      paddingRight: padding.horizontal,
                      paddingTop: padding.vertical,
                      paddingBottom: padding.vertical
                    }
                  }
                >
                  {row.format(item)}
                </td>
              ))}
            </tr>
          ))}
          {outputData.length === 0 && (
            <tr className={classes.tableRow}>
              <td className={classes.emptyTable} colSpan={formatRow.length}>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={classes.emptyIcon}
                />{' '}
                {emptyTableText ?? 'No data'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {pageInfo && (
        <div className={classes.pagination}>
          <div className={classes.pageToggles}>
            <Button
              archetype="flat"
              small
              onClick={onPreviousPage}
              disabled={pageInfo.offset === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <Button
              archetype="flat"
              small
              onClick={onNextPage}
              disabled={pageInfo.offset + pageInfo.limit >= pageInfo.total}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>

          <div className={classes.pageText}>
            {' '}
            {pageInfo.offset + 1}-{pageInfo.offset + pageInfo.given} of{' '}
            {pageInfo.total}
          </div>
        </div>
      )}
    </div>
  );
}
